/**
 * @fileoverview Jobs table filter persistence + bulk status helpers (MOR-2344)
 *
 * Pure helpers behind the Jobs list:
 * - The default filter state a user sees before they have saved any choices
 *   (Pre Appointment, Day One, and Active statuses).
 * - Reading / writing the user's filter choices to localStorage, keyed per
 *   user and organisation, with a defensive parse so a corrupt or stale
 *   value falls back to the defaults instead of breaking the table.
 * - Summarising the outcome of a bulk status update for the toast feedback.
 *
 * Used by: -ui.jobs-data-table.tsx, _layout.jobs.index.tsx
 */

import type { ColumnFiltersState } from "@tanstack/react-table";

import { extractErrorMessage } from "@/utils/errors";

/** The search text and column filters the Jobs table restores on return. */
export interface JobsFilterState {
  globalFilter: string;
  columnFilters: ColumnFiltersState;
}

/**
 * Statuses selected on a user's first visit. Pre Appointment is included so
 * pipeline jobs sit alongside the appointed work without a manual re-select.
 */
export const DEFAULT_JOB_STATUS_FILTER: ReadonlyArray<string> = [
  "pre_appointment",
  "day_one",
  "active",
];

export const DEFAULT_JOBS_FILTER_STATE: JobsFilterState = {
  globalFilter: "",
  columnFilters: [{ id: "status", value: [...DEFAULT_JOB_STATUS_FILTER] }],
};

/** Columns whose filter value is a list of selected option keys. */
const MULTI_SELECT_FILTER_IDS: ReadonlyArray<string> = [
  "status",
  "appointmentType",
  "createdAt",
  "lastActivityAt",
];

/** localStorage key — scoped so users and organisations never share filters. */
export function jobsFilterStorageKey(userId: string, orgId: string): string {
  return `jobs:filters:${userId}:${orgId}`;
}

function isStringArray(value: unknown): value is string[] {
  return (
    Array.isArray(value) && value.every((item) => typeof item === "string")
  );
}

/**
 * Keep only filter entries the table understands, in the shape it expects.
 * Unknown columns, malformed values, and empty selections are dropped.
 */
function sanitiseColumnFilters(value: unknown): ColumnFiltersState {
  if (!Array.isArray(value)) return [];
  return value.flatMap((entry: unknown): ColumnFiltersState => {
    if (typeof entry !== "object" || entry === null) return [];
    const id: unknown = "id" in entry ? entry.id : undefined;
    const filterValue: unknown = "value" in entry ? entry.value : undefined;
    if (id === "starred") {
      return filterValue === true ? [{ id, value: true }] : [];
    }
    if (typeof id === "string" && MULTI_SELECT_FILTER_IDS.includes(id)) {
      return isStringArray(filterValue) && filterValue.length > 0
        ? [{ id, value: filterValue }]
        : [];
    }
    return [];
  });
}

/**
 * Parse a stored filter state. Returns the defaults only when nothing has
 * been saved (or the value is unreadable) — an explicitly cleared selection
 * is restored as cleared.
 */
export function parseJobsFilterState(raw: string | null): JobsFilterState {
  if (raw === null) return DEFAULT_JOBS_FILTER_STATE;
  try {
    const parsed: unknown = JSON.parse(raw);
    if (typeof parsed !== "object" || parsed === null) {
      return DEFAULT_JOBS_FILTER_STATE;
    }
    const globalFilter: unknown =
      "globalFilter" in parsed ? parsed.globalFilter : "";
    const columnFilters: unknown =
      "columnFilters" in parsed ? parsed.columnFilters : [];
    return {
      globalFilter: typeof globalFilter === "string" ? globalFilter : "",
      columnFilters: sanitiseColumnFilters(columnFilters),
    };
  } catch {
    return DEFAULT_JOBS_FILTER_STATE;
  }
}

/** Read the saved filters, tolerating unavailable storage. */
export function loadJobsFilterState(key: string): JobsFilterState {
  try {
    return parseJobsFilterState(window.localStorage.getItem(key));
  } catch {
    return DEFAULT_JOBS_FILTER_STATE;
  }
}

/** Save the filters, ignoring unavailable storage (private mode, quota). */
export function saveJobsFilterState(key: string, state: JobsFilterState) {
  try {
    window.localStorage.setItem(key, JSON.stringify(state));
  } catch {
    /* ignore */
  }
}

// ---------------------------------------------------------------------------
// Bulk status update
// ---------------------------------------------------------------------------

/** A job the user selected for a bulk status change. */
export interface BulkStatusTarget {
  _id: string;
  jobName?: string;
  entityName: string;
  status: string;
}

export interface BulkStatusFailure {
  jobId: string;
  label: string;
  message: string;
}

export interface BulkStatusSummary {
  updated: number;
  /** Selected jobs already at the target status — nothing to change. */
  unchanged: number;
  failures: BulkStatusFailure[];
}

/** Jobs that actually need a write to reach `status`. */
export function jobsNeedingStatusChange<T extends BulkStatusTarget>(
  jobs: ReadonlyArray<T>,
  status: string,
): T[] {
  return jobs.filter((job) => job.status !== status);
}

/**
 * Combine the settled per-job updates into counts plus a readable failure
 * list. `results[i]` must correspond to `attempted[i]`.
 */
export function summariseBulkStatusUpdate(
  selectedCount: number,
  attempted: ReadonlyArray<BulkStatusTarget>,
  results: ReadonlyArray<PromiseSettledResult<unknown>>,
): BulkStatusSummary {
  const failures = attempted.flatMap((job, i): BulkStatusFailure[] => {
    const result = results[i];
    if (result === undefined || result.status === "fulfilled") return [];
    return [
      {
        jobId: job._id,
        label: job.jobName || job.entityName || "Untitled job",
        message: extractErrorMessage(result.reason, "Update failed"),
      },
    ];
  });
  return {
    updated: attempted.length - failures.length,
    unchanged: selectedCount - attempted.length,
    failures,
  };
}

const plural = (n: number) => `${n} job${n === 1 ? "" : "s"}`;

/** Toast headline for a successful (or partly successful) bulk update. */
export function bulkStatusSuccessMessage(
  summary: BulkStatusSummary,
  statusLabel: string,
): string {
  if (summary.updated === 0 && summary.failures.length === 0) {
    return `${plural(summary.unchanged)} already ${statusLabel}`;
  }
  const updated = `Updated ${plural(summary.updated)} to ${statusLabel}`;
  return summary.unchanged > 0
    ? `${updated} (${plural(summary.unchanged)} already ${statusLabel})`
    : updated;
}

/** Toast body listing the first few failures by job, then a remainder. */
export function bulkStatusFailureDescription(
  failures: ReadonlyArray<BulkStatusFailure>,
  limit = 3,
): string {
  const shown = failures
    .slice(0, limit)
    .map((failure) => `${failure.label}: ${failure.message}`);
  const remaining = failures.length - shown.length;
  return [...shown, ...(remaining > 0 ? [`…and ${remaining} more`] : [])].join(
    "\n",
  );
}
